import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { Cms, IBookWorkshopPageResponse, IDealership, IServiceBrand, ShopDto } from '@impact/data';
import { ImpactOverlayRef, OverlayService } from '@impactdk/ngx-overlay';
import { forkJoin, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { arrowRightIcon } from '../../../../icons';
import { GlobalDataService } from '../../../core/global-data.service';
import { WorkshopService } from '../../../core/workshop.service';
import { IBooking } from '../../../models/book-workshop.models';
import { BookWorkshopExternalComponent, IExternalBookingModalContent } from '../book-workshop-external.component';

export type IEnrichedWorkshop = Partial<ShopDto> & Omit<Partial<Cms.DealershipDto>, 'id'>;

@Component({
    selector: 'app-book-workshop-select-shop',
    template: `
        <section class="flow-step__header">
            <h1 class="h4">{{ 'book_workshop.select_workshop' | translate }}</h1>
            <div>{{ 'book_workshop.select_workshop_info' | translate }}</div>
        </section>

        <div class="book-workshop-select-shop-list" *ngIf="loading">
            <div class="loader"></div>
        </div>

        <div class="book-workshop-select-shop-list" *ngIf="!booking?.makeName || allShops?.length === unAuthorizedShops?.length">
            <ng-container *ngFor="let shop of allShops">
                <input class="book-workshop-select-shop-list-item__radio"
                    type="radio"
                    name="shop"
                    [id]="'shop__' + shop?.id!"
                    [value]="shop.id"
                    [(ngModel)]="selectedShopId" />

                <label *ngIf="shop"
                    [for]="'shop__' + shop?.id!"
                    (click)="setSelectedShop(shop)"
                    class="book-workshop-select-shop-list-item__content"
                    appCircleCheckmark
                    [condition]="shop?.id === selectedShop?.id">

                    <div class="book-workshop-select-shop-list-item__info">
                        <div class="book-workshop-select-shop-list-item__address">
                            <div class="book-workshop-select-shop-list-item__name">{{ shop.displayName }}</div>
                            <div>{{ shop.address1 }}</div>
                            <div>{{ shop.address2 }} {{ shop.address3 }}</div>
                        </div>
                        <div class="text-small text-secondary">
                            <div>{{ 'generic.phone_number_abbreviation' | translate }} {{ shop.telephone }}</div>
                            <div>{{ 'generic.email' | translate }} {{ shop.emailAddress }}</div>
                        </div>
                    </div>

                    <div class="book-workshop-select-shop-list-item__actions">
                        <app-button
                            (click)="setSelectedShop(shop)"
                            [title]="
                                shop?.id === selectedShop?.id
                                    ? ('checkout.insurance_step____button_option____choosen' | translate)
                                    : ('checkout.insurance_step____button_option____choose_this' | translate)
                            "
                            buttonClass="button--primary">
                        </app-button>
                    </div>
                </label>
            </ng-container>
        </div>

        <div class="book-workshop-select-shop-list" *ngIf="authorizedShops?.length">
            <section class="flow-step__header">
                <h5>{{ 'book_workshop.select_workshop____authorized_shops____headline' | translate: { brand: booking?.makeName } }}</h5>
                <p class="no-margin">
                    {{ 'book_workshop.select_workshop____authorized_shops____description' | translate: { brand: booking?.makeName } }}
                </p>
            </section>
            <ng-container *ngFor="let shop of authorizedShops">
                <input
                    class="book-workshop-select-shop-list-item__radio"
                    type="radio"
                    name="shop"
                    [id]="'shop__' + shop?.id!"
                    [value]="shop.id"
                    [(ngModel)]="selectedShopId"
                />
                <label
                    *ngIf="shop"
                    (click)="checkForExternalBookingFlow(shop?.id); setSelectedShop(shop)"
                    [for]="'shop__' + shop?.id!"
                    class="book-workshop-select-shop-list-item__content"
                    appCircleCheckmark
                    [condition]="shop?.id === selectedShop?.id">

                    <div class="book-workshop-select-shop-list-item__info">
                        <div class="book-workshop-select-shop-list-item__address">
                            <div class="book-workshop-select-shop-list-item__name">{{ shop.displayName }}</div>
                            <div>{{ shop.address1 }}</div>
                            <div>{{ shop.address2 }} {{ shop.address3 }}</div>
                        </div>
                        <div class="text-small text-secondary">
                            <div>{{ 'generic.phone_number_abbreviation' | translate }} {{ shop.telephone }}</div>
                            <div>{{ 'generic.email' | translate }} {{ shop.emailAddress }}</div>
                        </div>
                    </div>

                    <div class="book-workshop-select-shop-list-item__actions">
                        <div *ngIf="currentServiceBrand" class="book-workshop-select-shop-list-item__brand-logo">
                            <img [src]="currentServiceBrand?.logo?.src | resolveMediaUrl : { format: 'webp' }" loading="lazy" />
                        </div>

                        <app-button
                            (click)="setSelectedShop(shop)"
                            [title]="
                                shop?.id === selectedShop?.id
                                    ? ('checkout.insurance_step____button_option____choosen' | translate)
                                    : ('checkout.insurance_step____button_option____choose_this' | translate)
                            "
                            buttonClass="button--primary">
                        </app-button>
                    </div>
                </label>
            </ng-container>
        </div>
        <div class="book-workshop-select-shop-list" *ngIf="unAuthorizedShops?.length && unAuthorizedShops?.length !== allShops?.length">
            <section class="flow-step__header">
                <h5>{{ 'book_workshop.select_workshop____unauthorized_shops____headline' | translate: { brand: booking?.makeName } }}</h5>
                <p class="no-margin">
                    {{ 'book_workshop.select_workshop____unauthorized_shops____description' | translate: { brand: booking?.makeName } }}
                </p>
            </section>
            <ng-container *ngFor="let shop of unAuthorizedShops">
                <input
                    class="book-workshop-select-shop-list-item__radio"
                    type="radio"
                    name="shop"
                    [id]="'shop__' + shop?.id!"
                    [value]="shop.id"
                    [(ngModel)]="selectedShopId" />
                <label *ngIf="shop"
                    [for]="'shop__' + shop?.id!"
                    class="book-workshop-select-shop-list-item__content"
                    (click)="setSelectedShop(shop)"
                    appCircleCheckmark
                    [condition]="shop?.id === selectedShop?.id">

                    <div class="book-workshop-select-shop-list-item__info">
                        <div class="book-workshop-select-shop-list-item__address">
                            <div class="book-workshop-select-shop-list-item__name">{{ shop.displayName }}</div>
                            <div>{{ shop.address1 }}</div>
                            <div>{{ shop.address2 }} {{ shop.address3 }}</div>
                        </div>
                        <div class="text-small text-secondary">
                            <div>{{ 'generic.phone_number_abbreviation' | translate }} {{ shop.telephone }}</div>
                            <div>{{ 'generic.email' | translate }} {{ shop.emailAddress }}</div>
                        </div>
                    </div>
                    <div class="book-workshop-select-shop-list-item__actions">
                        <app-button
                            (click)="setSelectedShop(shop)"
                            [title]="shop?.id === selectedShop?.id
                                    ? ('checkout.insurance_step____button_option____choosen' | translate)
                                    : ('checkout.insurance_step____button_option____choose_this' | translate)"
                            buttonClass="button--primary">
                        </app-button>
                    </div>
                </label>
            </ng-container>
        </div>
        <div class="book-workshop-flow__bottom">
            <div class="book-workshop-flow__bottom-content">
                <app-button
                    buttonClass="book-workshop-stepper-content__continue-button button--tertiary"
                    (click)="previousStep.emit()"
                    [title]="'book_workshop.back' | translate"
                    [disabled]="!selectedShop"
                ></app-button>
                <app-button
                    buttonClass="book-workshop-stepper-content__continue-button button--primary"
                    [disabled]="!selectedShop || loading"
                    (handleClick)="onNextStepClick()"
                    [title]="'book_workshop.continue' | translate">
                    <i class="button__icon">${arrowRightIcon}</i>
                </app-button>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookWorkshopSelectShopComponent implements OnInit, OnDestroy {
    private readonly unsubscribe = new Subject<void>();

    @Input() booking: IBooking | null;
    @Input() pageContent: Cms.BookWorkshopPage;
    @Output() nextStep = new EventEmitter();
    @Output() previousStep = new EventEmitter();

    allShops?: IEnrichedWorkshop[];
    authorizedShops?: IEnrichedWorkshop[];
    unAuthorizedShops?: IEnrichedWorkshop[];
    currentServiceBrand?: Cms.ServiceMakeDto;
    selectedShopId = '';
    selectedShop?: IEnrichedWorkshop;
    loading = true;
    overlay: ImpactOverlayRef;
    externalBookingFlowOnly: boolean;
    
    constructor(
        private workshopService: WorkshopService,
        private globalDataService: GlobalDataService,
        private cdr: ChangeDetectorRef,
        private overlayService: OverlayService
    ) {}

    ngOnInit(): void {
        if (this.booking?.shop) {
            this.setSelectedShop(this.booking?.shop || undefined);
        }
        this.fetchAllShops();
    }

    fetchAllShops() {
        forkJoin({
            dracarShops: this.workshopService.getShops(),
            umbracoDealerships: this.globalDataService.getDealerships(),
        })
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(({ dracarShops, umbracoDealerships }) => {
                this.enrichWorkshops(dracarShops, umbracoDealerships?.children);
            });
    }

    // Merge data with umbraco shops
    // Here the shops get their display name from umbraco dealerships data
    enrichWorkshops(workshops: ShopDto[], dealerShips?: Cms.DealershipDto[]) {
        this.allShops = workshops.map((ws) => ({
            ...ws,
            ...dealerShips?.find((d) => d.dracarID === ws.id) 
        }));
        this.sortWorkshops();
    }

    sortWorkshops() {
        if (this.booking?.makeName) {
            this.authorizedShops = this.allShops?.filter(
                (shop) =>
                    !!shop.serviceMakes?.find((brand) => {
                        const match = brand.makeTag?.displayName?.toLowerCase() === this.booking?.makeName?.toLowerCase();
                        if (match) {
                            this.currentServiceBrand = brand;
                        }
                        return match;
                    })
            );
            this.unAuthorizedShops = this.allShops?.filter(
                (shop) => !shop.serviceMakes?.find((brand) => brand.makeTag?.displayName?.toLowerCase() === this.booking?.makeName?.toLowerCase())
            );
        }

        this.loading = false;
        this.cdr.markForCheck();
    }

    setSelectedShop(shop?: IEnrichedWorkshop) {
        this.selectedShop = shop || undefined;
        this.selectedShopId = shop?.id || '';
        this.cdr.markForCheck();
    }

    onNextStepClick() {
        this.nextStep.emit({
            shop: this.selectedShop,
        });
    }

    checkForExternalBookingFlow(shopID?: string | null) {
        const selectedShop = this.authorizedShops?.find((s) => s.id === shopID);
        const serviceBrand = selectedShop?.serviceMakes?.find(
            (SB) => SB.makeTag?.displayName?.toLowerCase() === this.booking?.makeName?.toLowerCase()
        );

        if (serviceBrand?.externalFlowLink?.length) {
            this.externalBookingFlowOnly = !!serviceBrand?.forceExternalFlow;

            this.openExternalBookingDialog({
                carBrand: this.booking?.makeName,
                externalBookingUrl: `${serviceBrand?.externalFlowLink}`,
                forceExternalFlow: serviceBrand?.forceExternalFlow ?? false,
            });
        }
    }

    private openExternalBookingDialog({ carBrand, externalBookingUrl, forceExternalFlow }: IExternalBookingModalContent) {
        this.overlay = this.overlayService.open(BookWorkshopExternalComponent, {
            data: {
                pageContent: this.pageContent,
                carBrand,
                externalBookingUrl,
                regNumber: this.booking?.regNumber,
                forceExternalFlow,
            },
        });

        this.overlay
            .afterClose()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(() => {
                if (this.externalBookingFlowOnly) {
                    this.setSelectedShop(undefined);
                    this.externalBookingFlowOnly = false;
                }
            });
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
